/**
 * @file UserManualLink.tsx
 * @description
 * -----------------------------------------------------
 * Archivo que contiene el link para descargar el manual de usuario (dependiendo del rol)
 * -----------------------------------------------------
 * @version 0.0.1a
 * @created 2025-11-04
 * @author DLTCode
 * @copyright © DLTCode
 * @license Private
 * */

"use client";

import Link from "next/link";
import { useTranslations } from "next-intl";
import { useCallback, useTransition } from "react";
import { toast } from "sonner";
type UserManualLinkProps = {
  className?: string;
};
export default function UserManualLink({
  className
}: UserManualLinkProps) {
  const [isPending, startTransition] = useTransition();
  const t = useTranslations("Footer");
  const responseMessages = useTranslations("ResponseMessages");
  const handleManualDownload = useCallback(async (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    startTransition(async () => {
      try {
        const response = await fetch("/api/documents/user-manual");
        if (!response.ok) {
          throw new Error(`Error ${response.status}: ${await response.text()}`);
        }

        // Obtener el nombre del archivo del encabezado Content-Disposition si está disponible
        let fileName = "manual.pdf";
        const contentDisposition = response.headers.get("Content-Disposition");
        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
          if (fileNameMatch && fileNameMatch[1]) {
            fileName = fileNameMatch[1];
          }
        }

        // Convertir respuesta a blob
        const blob = await response.blob();

        // Crear enlace de descarga y activar la descarga
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.style.display = "none";
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success(responseMessages("USER_MANUAL_DOWNLOADED"));
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        toast.error(responseMessages("USER_MANUAL_DOWNLOAD_ERROR"));
      }
    });
  }, [responseMessages]);
  return <Link href="#" onClick={handleManualDownload} style={{
    cursor: isPending ? "wait" : "pointer",
    opacity: isPending ? 0.7 : 1
  }} className={className}>
      {t("userManual")}
    </Link>;
}